/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Component, inject, OnInit} from '@angular/core';
import {
  ListBatteryObject,
  LoadListParams,
} from '../../../../models/ListBattery';
import {PassportService} from '../../../../services/passport.service';
import {TranslateService} from '@ngx-translate/core';
import {ToastService} from '@siemens/ix-angular';
import {AppSettings} from '../../../../models/Constants';
import {Router} from '@angular/router';

type DeletedPassport = {
  passport: ListBatteryObject;
  readonly childCount: number;
};

@Component({
  selector: 'app-list-removed-battery-passes',
  templateUrl: './list-removed-battery-passes.component.html',
  styleUrl: './list-removed-battery-passes.component.scss',
  standalone: false,
})
export class ListRemovedBatteryPassesComponent implements OnInit {
  private readonly passportService = inject(PassportService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly toastService = inject(ToastService);

  passportList: DeletedPassport[] = [];

  loadingInitial = true;
  loadingMore = false;
  loadingOffset = 0;
  allLoaded = false;

  ngOnInit(): void {
    this.loadingInitial = true;
    this.loadMorePassports().then(() => {
      this.loadingInitial = false;
    });
  }

  async onRestoreButtonClicked(pass: ListBatteryObject) {
    await this.passportService.restore(pass.id);
    const content = pass.metadata.name ? `"${pass.metadata.name}"` : pass.id;
    this.translate
      .get('battery_pass.removed_list.restored_success', {id: content})
      .subscribe((msg: string) => {
        this.toastService.show({message: msg, type: 'success'});
      });
    this.passportList = this.passportList.filter(
      (p) => p.passport.id !== pass.id,
    );
  }

  async loadMorePassports() {
    this.loadingMore = true;
    const loadListParams: LoadListParams = {
      limit: AppSettings.BATTERY_LIST_PAGE_SIZE,
      offset: this.loadingOffset,
      state: 'inactive',
      topLevelOnly: true,
    };
    const passportRequest = await this.passportService.loadList(loadListParams);

    for (const passport of passportRequest.data) {
      const childCount = passportRequest.meta?.childCounts?.[passport.id] ?? 0;
      this.passportList.push({passport: passport, childCount: childCount});
    }

    const totalPassports = passportRequest.meta?.page?.totalElements ?? 0;
    const hasMoreToLoad =
      this.loadingOffset + AppSettings.BATTERY_LIST_PAGE_SIZE < totalPassports;

    if (hasMoreToLoad) {
      this.loadingOffset += AppSettings.BATTERY_LIST_PAGE_SIZE;
    } else {
      this.allLoaded = true;
      this.loadingOffset = totalPassports;
    }

    this.loadingMore = false;
  }
}
